import React from 'react';
import { EditableText } from '../ui/EditableText';
import { useBrand } from '@/context/BrandContext';
import { BrandSectionLayout } from './BrandSectionLayout';
import { X } from 'lucide-react';

interface TargetAudienceSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const TargetAudienceSection: React.FC<TargetAudienceSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();

  // Get primary color from brand data
  const primaryColor = state.brandData?.colors?.primary || '#3B82F6';

  // Parse audiences from JSON string
  const audiences = section.content.audiences
    ? (typeof section.content.audiences === 'string'
        ? JSON.parse(section.content.audiences)
        : section.content.audiences)
    : ['Young Professionals', 'Students', 'Entrepreneurs', 'Creatives'];

  const handleAddAudience = () => {
    const newAudiences = [...audiences, 'New Audience'];
    updateSectionContent('audiences', JSON.stringify(newAudiences));
  };

  const handleRemoveAudience = (index: number) => {
    const newAudiences = audiences.filter((_: string, i: number) => i !== index);
    updateSectionContent('audiences', JSON.stringify(newAudiences));
  };

  const handleUpdateAudience = (index: number, value: string) => {
    const newAudiences = [...audiences];
    newAudiences[index] = value;
    updateSectionContent('audiences', JSON.stringify(newAudiences));
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Main container */}
      <div className="h-full flex flex-col justify-between py-8">
        {/* Top Section - Title and Description */}
        <div className="space-y-6">
          {/* Title */}
          <EditableText
            value={section.content.sectionTitle || 'Target Audience'}
            onChange={(value) => updateSectionContent('sectionTitle', value)}
            element="h3"
            className="text-4xl font-bold leading-tight"
            placeholder="Target Audience"
            editModeEnabled={isEditing}
            isEditing={isEditing && editingElement === 'sectionTitle'}
            onEditStart={() => setEditingElement('sectionTitle')}
            onEditEnd={() => setEditingElement(null)}
            onStyleChange={(styles) => updateSectionStyles('sectionTitle', styles)}
            initialStyles={{
              fontWeight: 'bold',
              lineHeight: 1.2,
              fontFamily: state.brandData?.globalTitleFont || 'Inter',
              ...section.content.sectionTitleStyles
            }}
            isGlobalDarkMode={isGlobalDarkMode}
            sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
          />

          {/* Description */}
          <EditableText
            value={section.content.description || 'Define the key demographic groups and personas that your brand aims to reach and engage with.'}
            onChange={(value) => updateSectionContent('description', value)}
            element="p"
            className="text-lg leading-relaxed"
            placeholder="Add description..."
            multiline={true}
            editModeEnabled={isEditing}
            isEditing={isEditing && editingElement === 'description'}
            onEditStart={() => setEditingElement('description')}
            onEditEnd={() => setEditingElement(null)}
            onStyleChange={(styles) => updateSectionStyles('description', styles)}
            initialStyles={{
              fontFamily: state.brandData?.globalTextFont || 'Inter',
              opacity: 0.8,
              maxWidth: '550px',
              ...section.content.descriptionStyles
            }}
            isGlobalDarkMode={isGlobalDarkMode}
            sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
          />
        </div>

        {/* Bottom Section - Audience Circles */}
        <div
          className="grid mt-auto pt-8 gap-4"
          style={{
            gridTemplateColumns: `repeat(${Math.min(audiences.length + (isEditing ? 1 : 0), 6)}, 1fr)`
          }}
        >
          {audiences.map((audience: string, index: number) => (
            <div key={index} className="relative group flex justify-center">
              {/* Circle with text */}
              <div
                className="rounded-full flex items-center justify-center p-8 text-center relative aspect-square w-full"
                style={{
                  border: `4px solid ${primaryColor}`,
                  transition: 'transform 0.2s ease'
                }}
              >
                <EditableText
                  value={audience}
                  onChange={(value) => handleUpdateAudience(index, value)}
                  element="span"
                  className="font-semibold text-4xl md:text-3xl lg:text-4xl leading-tight"
                  placeholder="Audience"
                  editModeEnabled={isEditing}
                  isEditing={isEditing && editingElement === `audience-${index}`}
                  onEditStart={() => setEditingElement(`audience-${index}`)}
                  onEditEnd={() => setEditingElement(null)}
                  initialStyles={{
                    fontWeight: '600',
                    textAlign: 'center',
                    fontFamily: state.brandData?.globalTextFont || 'Inter',
                    ...section.content[`audience${index}Styles`]
                  }}
                  isGlobalDarkMode={isGlobalDarkMode}
                  sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
                />

                {/* Remove button - only visible in edit mode */}
                {isEditing && audiences.length > 1 && (
                  <button
                    onClick={() => handleRemoveAudience(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    title="Remove audience"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add new audience button - only visible in edit mode */}
          {isEditing && (
            <div className="flex justify-center">
              <button
                onClick={handleAddAudience}
                className="rounded-full border-4 border-dashed flex items-center justify-center text-center hover:border-solid transition-all aspect-square w-full"
                style={{
                  borderColor: primaryColor,
                  color: primaryColor
                }}
              >
                <span className="text-4xl font-light">+</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </BrandSectionLayout>
  );
};
